/**
 * ErrorBoundary — iter64 P0.
 *
 * The weekend tester reported a blank screen after creating a shift,
 * recoverable only by logout + login. The root cause is a downstream
 * render error that propagated past the schedule page. Wrapping the
 * schedule (and any other page prone to receiving freshly-shaped data)
 * in this boundary preserves the shell + lets the user retry without
 * losing their session.
 */
import React from 'react';
import { Button } from './ui/button';
import { AlertTriangle } from 'lucide-react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || 'Something went wrong' };
  }

  componentDidCatch(error, info) {
    // Keep this quiet in prod — toasts are already in place for user-facing
    // calls; this boundary is a safety net for React render crashes.
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, info);
  }

  handleReset = () => {
    this.setState({ hasError: false, message: '' });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="flex flex-col items-center justify-center min-h-[50vh] p-8 text-center"
          data-testid="error-boundary-fallback"
        >
          <AlertTriangle className="w-12 h-12 text-[#FFC04D] mb-4" />
          <h2 className="text-xl font-semibold mb-2">Something went wrong on this page</h2>
          <p className="text-white/60 mb-6 max-w-md">
            {this.state.message} — the error has been logged. You can keep working;
            try again or reload the page without signing out.
          </p>
          <div className="flex gap-3">
            <Button
              onClick={this.handleReset}
              className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
              data-testid="error-boundary-retry"
            >
              Try again
            </Button>
            <Button
              variant="outline"
              onClick={() => window.location.reload()}
              data-testid="error-boundary-reload"
            >
              Reload page
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
